import { availableAmountsOfHours, getConvertedAlertFieldSettings } from './core/alerts/alerts';
import { exportFormats, exportTimeSteps, getExportMetricsOptions } from './core/export-menu';
import { allowedDssView, allowedHistoryPeriods, allowedIrrigationDashboardGraphPeriods } from './core/history/history';
import * as enums from './enums';
import { BillingStatus } from './enums';
import { commons, measurements } from './enums/MetricIds';
import { alertCompatibleAppleScabLevels } from './resources/alerts.types';
import { accumulationMetrics } from './resources/cumulativeSum';
import { EnvironmentType, IrrigationType, SoilTexture, SubstrateType } from './resources/plots';
import { thresholdTypes } from './resources/thresholds.types';
import { WaterBalanceSoilType } from './resources/waterBalance';
import { arvalisCropTypes, possibleCIPANDevelopment, seedCornSizes, sorghoPrecocities, wbArvalisDepth, wbArvalisHydromorphy, wbArvalisLimestone, wbArvalisSoilTexture, wbArvalisStoniness } from './resources/waterBalanceArvalis.type';
import superscheme, { validators as v } from './superscheme';
const baseValidator = v.between({
    max: 20,
    min: 0,
    errorMessageKey: 'help.cumulative_sum_base_error_message',
    strict: false
});
const GDDTMaxValidator = v.between({
    max: 40,
    min: 20,
    errorMessageKey: 'help.cumulative_sum_threshold_error_message',
    strict: false
});
const weenatWBSoilTypes = Object.values(WaterBalanceSoilType);
// We filter otherwise we would also get the string keys as values
const availableSoilTextures = Object.values(SoilTexture)
    .map((x) => parseInt(x))
    .filter((x) => !isNaN(x));
const availableEnvironments = Object.values(EnvironmentType)
    .map((x) => parseInt(x))
    .filter((x) => !isNaN(x));
const availableSubstrateType = Object.values(SubstrateType)
    .map((x) => parseInt(x))
    .filter((x) => !isNaN(x));
const availableIrrigationTypes = [
    IrrigationType.drip,
    IrrigationType.other,
    IrrigationType.sprinkle,
    -1
];
const availableMembersRoles = [
    enums.OrgRole.viewer,
    enums.OrgRole.agent,
    enums.OrgRole.admin
];
const exportMetrics = getExportMetricsOptions().map((opt) => opt.value);
const schemas = {
    members: {
        updateRole: superscheme({
            role: v.enum(availableMembersRoles)
        }),
        invite: superscheme({
            email: v.email,
            role: v.enum(availableMembersRoles)
        })
    },
    auth: {
        login: superscheme({
            email: v.email,
            password: v.nonEmptyString
        }),
        register: superscheme({
            firstName: v.nonEmptyString,
            lastName: v.nonEmptyString,
            email: v.email,
            password: v.nonEmptyString,
            country: v.nonEmptyString,
            telephoneNumber: v.nonEmptyString,
            telephoneCountryCode: v.nonEmptyString,
            isSubscribedToNewsletter: v.boolean,
            hasAcceptedTermsOfService: v.isTrue
        }),
        resetPasswordConfirm: superscheme({
            password: v.nonEmptyString
        }),
        changePassword: superscheme({
            oldPassword: v.nonEmptyString,
            newPassword: v.nonEmptyString
        })
    },
    billing: {
        editBillingAccount: superscheme({
            addressLine1: v.addressLine1(true),
            addressLine2: v.addressLine2,
            city: v.city(true),
            country: v.sizedString({ minChars: 2, maxChars: 2, required: true }),
            organizationName: v.sizedString({ minChars: 1, maxChars: 128, required: true }),
            siret: v.sizedString({ minChars: 1, maxChars: Number.MAX_SAFE_INTEGER, required: true }),
            zipCode: v.zipCode(true),
            vatNumber: v.optionalString
        }),
        editSiret: superscheme({
            siret: v.nonEmptyString
        }),
        history: superscheme({
            year: v.optionalNumber
        })
    },
    cropTracking: {
        createCrop: superscheme({
            seedId: v.number
        }),
        createCycle: superscheme({ startingDate: v.date }),
        closeCycle: superscheme({ closingDate: v.date }),
        fillStageDate: superscheme({ date: v.date })
    },
    measurementsReports: {
        dripIrrigation: superscheme({
            date: v.date,
            /** hours */
            wateringDuration: v.number,
            /** L/h */
            waterFlow: v.number
        }),
        irrigationAndRainfalls: superscheme({
            irrigation: v.optionalNumber,
            rainfalls: v.optionalNumber
        }),
        updateIrrigation: superscheme({
            irrigation: v.number
        }),
        updateRainfalls: superscheme({
            rainfalls: v.number
        }),
        updateIrrigationAndDate: superscheme({
            irrigation: v.number,
            date: v.date
        }),
        updateRainfallsAndDate: superscheme({
            rainfalls: v.number,
            date: v.date
        }),
        addWatering: superscheme({
            watering: v.number,
            date: v.date,
            type: v.wateringType
        }),
        addDryMatterReport: superscheme({
            dryMatterPercentage: v.percentage(),
            date: v.date
        })
    },
    waterBalance: {
        configure: superscheme({
            soilType: v.enum(weenatWBSoilTypes),
            availableWaterCapacity: v.number
        }),
        arvalisPlotSettings: superscheme({
            // Plot details
            plotArea: v.number,
            soilCode: v.optionalString,
            valuesFromArvalis: v.boolean,
            ruMax: v.optionalNumber,
            rfuMax: v.RFU,
            // Previous crop
            previousWatering: v.boolean,
            previousCrop: v.string,
            // Intermediary crop (OPTIONAL)
            startDate: v.optionalDate,
            raisingDate: v.optionalDate,
            destructionDate: v.optionalDate,
            development: v.optionalNumber,
            // Current crop
            cropType: v.cropType,
            departmentCode: v.optionalString,
            sowingDate: v.date,
            // Crop characteristics (OPTIONAL)
            variety: v.optionalString,
            supersweet: v.optionalBoolean,
            precocity: v.optional(v.enum(sorghoPrecocities)),
            numberOfDaysBtwSowingFlowering: v.optionalNumber,
            gddBtwSowingFlowering: v.optionalNumber,
            size: v.optional(v.enum(seedCornSizes)),
            femaleGenitor: v.optionalString,
            maleGenitor: v.optionalString,
            m1SowingDate: v.optionalDate,
            m2SowingDate: v.optionalDate,
            m3SowingDate: v.optionalDate,
            sowingDevice: v.optionalString,
            // Irrigation
            daysBetweenWatering: v.optionalNumber,
            wateringAmount: v.optionalNumber,
            quota: v.optionalNumber,
            // External Station
            useExternalStationForWaterBalance: v.boolean,
            brand: v.isOptionalOrNot({
                checkedName: 'useExternalStationForWaterBalance',
                validation: v.number
            }),
            serialNumber: v.isOptionalOrNot({
                checkedName: 'useExternalStationForWaterBalance',
                validation: v.nonEmptyString
            })
        }),
        arvalisNewSeasonPlotInfos: superscheme({
            // Plot details
            plotArea: v.number,
            soilCode: v.optionalString,
            valuesFromArvalis: v.boolean,
            ruMax: v.optionalNumber,
            rfuMax: v.RFU,
            // Irrigation
            daysBetweenWatering: v.optionalNumber,
            wateringAmount: v.optionalNumber,
            quota: v.optionalNumber,
            // External Station
            useExternalStationForWaterBalance: v.boolean,
            brand: v.isOptionalOrNot({
                checkedName: 'useExternalStationForWaterBalance',
                validation: v.number
            }),
            serialNumber: v.isOptionalOrNot({
                checkedName: 'useExternalStationForWaterBalance',
                validation: v.nonEmptyString
            })
        }),
        arvalisNewSeasonSeasonInfos: superscheme({
            // Previous crop
            previousWatering: v.boolean,
            previousCrop: v.nonEmptyString,
            // Intermediary crop (OPTIONAL)
            startDate: v.optionalDate,
            raisingDate: v.optionalDate,
            destructionDate: v.optionalDate,
            development: v.optionalNumber,
            // Current crop
            cropType: v.cropType,
            sowingDate: v.date,
            departmentCode: v.string,
            // Crop characteristics (OPTIONAL)
            variety: v.variety,
            supersweet: v.optionalBoolean,
            precocity: v.optional(v.enum(sorghoPrecocities)),
            numberOfDaysBtwSowingFlowering: v.optionalNumber,
            gddBtwSowingFlowering: v.optionalNumber,
            size: v.optional(v.enum(seedCornSizes)),
            femaleGenitor: v.optionalString,
            maleGenitor: v.optionalString,
            m1SowingDate: v.optionalDate,
            m2SowingDate: v.optionalDate,
            m3SowingDate: v.optionalDate,
            sowingDevice: v.optionalString
        }),
        arvalisClientCreation: superscheme({
            city: v.city(false),
            firstName: v.nonEmptyString,
            lastName: v.nonEmptyString,
            mail: v.email,
            zipCode: v.zipCode(false)
        }),
        arvalisMonoCrop: superscheme({
            cropType: v.enum(arvalisCropTypes)
        }),
        arvalisPlotCreation: {
            plotDetails: superscheme({
                plotArea: v.number,
                soilCode: v.optionalString
            }),
            filterSoils: superscheme({
                stoniness: v.array(v.enum(wbArvalisStoniness)),
                texture: v.array(v.enum(wbArvalisSoilTexture)),
                depth: v.array(v.enum(wbArvalisDepth)),
                hydromorphy: v.enum(wbArvalisHydromorphy),
                limestone: v.enum(wbArvalisLimestone)
            }),
            previousCrop: superscheme({
                previousWatering: v.boolean,
                previousCrop: v.string
            }),
            intermediaryCrop: superscheme({
                startDate: v.date,
                raisingDate: v.date,
                destructionDate: v.date,
                development: v.enum(possibleCIPANDevelopment)
            }),
            currentCrop: superscheme({
                cropType: v.cropType,
                sowingDate: v.date,
                departmentCode: v.optionalString
            }),
            currentCropDetails: {
                default: superscheme({
                    variety: v.string
                }),
                haricot: superscheme({
                    variety: v.optionalString
                }),
                sorgho: superscheme({
                    variety: v.optionalString,
                    precocity: v.enum(sorghoPrecocities)
                }),
                sweetCorn: superscheme({
                    variety: v.string,
                    supersweet: v.boolean
                }),
                seedCorn: superscheme({
                    numberOfDaysBtwSowingFlowering: v.optionalNumber,
                    gddBtwSowingFlowering: v.optionalNumber,
                    size: v.enum(seedCornSizes),
                    femaleGenitor: v.optionalString,
                    maleGenitor: v.optionalString,
                    m1SowingDate: v.date,
                    m2SowingDate: v.optionalDate,
                    m3SowingDate: v.optionalDate,
                    sowingDevice: v.optionalString
                })
            },
            irrigationDetails: superscheme({
                daysBetweenWatering: v.number,
                wateringAmount: v.number,
                quota: v.number
            }),
            waterBalanceData: superscheme({
                useExternalStationForWaterBalance: v.boolean,
                brand: v.isOptionalOrNot({
                    checkedName: 'useExternalStationForWaterBalance',
                    validation: v.number
                }),
                serialNumber: v.isOptionalOrNot({
                    checkedName: 'useExternalStationForWaterBalance',
                    validation: v.nonEmptyString
                })
            })
        },
        arvalisEditAvailableWaterCapacity: superscheme({
            valuesFromArvalis: v.boolean,
            ruMax: v.optionalNumber,
            rfuMax: v.RFU,
            soilCode: v.optionalString
        })
    },
    maizy: {
        setDryMatterTarget: superscheme({
            dryMatterTarget: v.percentage()
        }),
        selectSeed: superscheme({
            seedId: v.number
        })
    },
    searchQuery: superscheme({ searchQuery: v.string }),
    decitrait: {
        createSanitaryStateNote: superscheme({
            date: v.date,
            leaves: v.number,
            grapes: v.number
        }),
        addProductDose: superscheme({
            quantity: v.number
        }),
        addProduct: superscheme({
            mildewProductIds: v.array(v.nonEmptyString),
            oidiumProductIds: v.array(v.nonEmptyString)
        }),
        addTreatment: superscheme({
            treatmentDate: v.date,
            foliageHeight: v.number,
            foliageWidth: v.number,
            mildewPressure: v.nonEmptyString,
            oidiumPressure: v.nonEmptyString
        }),
        connect: superscheme({
            epicureUsername: v.nonEmptyString,
            password: v.nonEmptyString
        }),
        createUser: superscheme({
            email: v.email,
            epicureUsername: v.epicureUsername,
            firstName: v.sizedString({ minChars: 2, maxChars: 30 }),
            lastName: v.sizedString({ minChars: 2, maxChars: 30 }),
            password: v.nonEmptyString
        }),
        createPlot: superscheme({
            name: v.nonEmptyString,
            plotArea: v.number,
            harvestDate: v.date,
            trainingMethod: v.nonEmptyString,
            interPlant: v.number,
            interRow: v.number,
            mildewSensibility: v.nonEmptyString,
            oidiumSensibility: v.nonEmptyString,
            hadOidiumAttack: v.boolean
        }),
        configureUser: superscheme({
            areaId: v.nonEmptyString,
            departmentId: v.nonEmptyString,
            town: v.nonEmptyString,
            postalCode: v.optionalNumber,
            responsable: v.nonEmptyString,
            vineyardCastle: v.string
        }),
        linkPlot: superscheme({
            decitraitPlotId: v.nonEmptyString
        })
    },
    irrigation: {
        capacitiveIrrigation: superscheme({
            saturation: v.between({
                min: 'comfort',
                max: 101,
                errorMessageKey: 'dss.capacitiveIrrigation.dss.errorSaturation'
            }),
            comfort: v.between({
                min: 'vigilance',
                max: 'saturation',
                errorMessageKey: 'dss.capacitiveIrrigation.dss.errorComfort'
            }),
            vigilance: v.between({
                min: 'stress',
                max: 'comfort',
                errorMessageKey: 'dss.capacitiveIrrigation.dss.errorVigilance'
            }),
            stress: v.between({
                min: -1,
                max: 'vigilance',
                errorMessageKey: 'dss.capacitiveIrrigation.dss.errorStress'
            })
        }),
        irritensio: superscheme({
            comfort: v.between({
                min: -1,
                max: 'vigilance',
                errorMessageKey: 'dss.irritensio.dss.errorComfort'
            }),
            vigilance: v.between({
                min: 'comfort',
                max: 'stress',
                errorMessageKey: 'dss.irritensio.dss.errorVigilance'
            }),
            stress: v.between({
                min: 'vigilance',
                max: 201,
                errorMessageKey: 'dss.irritensio.dss.errorStress'
            })
        }),
        plotRequirements: superscheme({
            environment: v.enum(availableEnvironments),
            irrigationType: v.enum(availableIrrigationTypes),
            cropType: v.number,
            soilTexture: v.enum(availableSoilTextures),
            soilTextureComposition: v.optionalObject({
                silt: v.percentage(),
                sand: v.percentage(),
                clay: v.percentage()
            })
        }),
        fertigationPlotRequirements: superscheme({
            environment: v.enum(availableEnvironments),
            irrigationType: v.enum(availableIrrigationTypes),
            cropType: v.number,
            substrate: v.enum(availableSubstrateType)
        })
    },
    dashboards: {
        irriGraphSettings: superscheme({
            periodId: v.enum(allowedIrrigationDashboardGraphPeriods)
        }),
        irrigation: superscheme({
            type: v.number
        }),
        capacitiveEquation: superscheme({
            equation: v.number
        }),
        environmentType: superscheme({
            environment: v.enum(availableEnvironments)
        }),
        substrateType: superscheme({
            substrate: v.enum(availableSubstrateType)
        })
    },
    plots: {
        updateSoilTexture: superscheme({
            soilTexture: v.enum(availableSoilTextures)
        }),
        deviceLink: superscheme({
            linkDate: v.optionalDate
        }),
        soilTextureComposition: superscheme({
            soilTextureComposition: v.soilTextureComposition
        }),
        create: superscheme({
            plotName: v.nonEmptyString
        })
    },
    createStationFromSensor: superscheme({
        childOrganizationId: v.optionalNumber,
        organizationId: v.number,
        deviceId: v.number,
        name: v.nonEmptyString
    }),
    options: {
        resellerForm: superscheme({
            reseller: v.nonEmptyString
        }),
        grapeVisionIdForm: superscheme({
            // See https://gitlab.com/weenat-back/core/-/merge_requests/1984/diffs#7c41aeef22bc4c8f48df9aaa19b1ecff98d51cae_892_895
            code: v.sizedString({ minChars: 1, maxChars: 30 })
        })
    },
    networkOrgs: {
        editPlan: superscheme({
            /** In edition mode, downgrading is not allowed, but undefined is ok, it means nothing selected hence, no billing status change */
            billingStatus: v.optional(v.enum([BillingStatus.expert, BillingStatus.plus]))
        }),
        edit: superscheme({
            name: v.nonEmptyString,
            addressLine1: v.addressLine1(),
            addressLine2: v.addressLine2,
            /** @deprecated TODO: remove once separated networkOrgs.editPlan on master */
            billingStatus: v.optional(v.enum([BillingStatus.expert, BillingStatus.plus])),
            membershipCode: v.membershipCode,
            zipCode: v.zipCode(),
            city: v.city(),
            countryCode: v.nonEmptyString,
            siret: v.optionalString
        })
    },
    devices: {
        create: superscheme({ serial: v.nonEmptyString }),
        changeBattery: superscheme({ changeDate: v.date }),
        changeHorizon: superscheme({ horizonId: v.number }),
        filter: superscheme({
            brand: v.optional(v.number),
            metrics: v.optional(v.array(v.string)),
            organizationId: v.optional(v.number)
        })
    },
    network: {
        inviteOrg: superscheme({
            firstName: v.optionalString,
            lastName: v.optionalString,
            organizationName: v.optionalString,
            telephoneNumber: v.optionalString,
            telephoneCountryCode: v.optionalString,
            membershipCode: v.membershipCode,
            billingStatus: v.billingStatus,
            email: v.email,
            isTrial: v.boolean
        }),
        requestMembership: superscheme({ organizationId: v.number })
    },
    me: {
        name: superscheme({
            firstName: v.nonEmptyString,
            lastName: v.nonEmptyString
        }),
        editNewsletterSubscription: superscheme({ isSubscribedToNewsletter: v.boolean }),
        editMyPreferences: superscheme({
            favoriteOrgId: v.optionalNumber,
            rainUnit: v.optionalNumber,
            temperatureUnit: v.optionalNumber,
            speedUnit: v.optionalNumber,
            electroConductivityUnit: v.optionalNumber,
            language: v.language,
            distance: v.optionalNumber
        }),
        editSecondaryPhoneNumbers: superscheme({
            telephoneNumbers: v.array(v.phoneNumber)
        }),
        newPhoneNumber: superscheme({
            telephoneCountryCode: v.nonEmptyString,
            enabled: v.boolean,
            telephoneNumber: v.nonEmptyString
        }),
        notificationsPreferences: superscheme({
            channel_0: v.boolean,
            channel_1: v.boolean,
            channel_2: v.boolean
        })
    },
    cumulativeSum: superscheme({
        base: v.between({
            max: 20,
            min: 0,
            errorMessageKey: 'help.cumulative_sum_base_error_message',
            strict: false
        }),
        startDate: v.date,
        coldTemperatureThreshold: v.between({
            max: 20,
            min: 0,
            errorMessageKey: 'help.cumulative_sum_base_error_message',
            strict: false
        }),
        metric: v.enum(accumulationMetrics)
    }),
    graphCreation: superscheme({
        dssView: v.enum(allowedDssView),
        metrics: v.array(v.enum([...commons, ...measurements, 'etp'])),
        periodId: v.enum(allowedHistoryPeriods)
    }),
    setBase: superscheme({
        base: baseValidator
    }),
    setGDDTMax: superscheme({
        GDDTMax: GDDTMaxValidator
    }),
    setGDDParameters: superscheme({
        base: baseValidator,
        GDDTMax: GDDTMaxValidator
    }),
    alertCreation: {
        notificationMethod: superscheme({
            isVoiceCallEnabled: v.boolean,
            telephoneNumber: v.nonEmptyString,
            telephoneCountryCode: v.nonEmptyString
        }),
        validation: superscheme({
            name: v.nonEmptyString,
            isVoiceCallEnabled: v.boolean,
            isActive: v.boolean
        }),
        predicateParamsSetups: {
            setupThreshold: superscheme({
                threshold: v.number
            }),
            /** For humidity thresholds forms */
            setupHumidityThresholdParams: superscheme({
                threshold: v.percentage()
            }),
            /** For rain sums forms */
            setupRainCumulParams: superscheme({
                threshold: v.between({
                    min: 1,
                    max: 10000,
                    errorMessageKey: 'forms.field_is_invalid',
                    strict: false
                }),
                since: v.date,
                isRepeatable: v.boolean
            }),
            /** For temperature sums forms */
            setupGddCumulParams: superscheme({
                threshold: v.between({
                    min: 10,
                    max: 10000,
                    errorMessageKey: 'forms.field_is_invalid',
                    strict: false
                }),
                base: baseValidator,
                since: v.date,
                isRepeatable: v.boolean
            }),
            /** For water status forms */
            setupWaterStatusParams: superscheme({
                horizons: v.nonEmptyArray(v.number),
                thresholds: v.nonEmptyArray(v.enum(thresholdTypes))
            }),
            /** For forecast forms */
            setupForecastedParams: superscheme({
                amountInHours: v.enum(availableAmountsOfHours)
            }),
            /** for leaf wetness threshold forms */
            setupLeafWetnessThresholdParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupLeafWetnessThresholdParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For soil temperature thresholds forms */
            setupSoilTemperatureThresholdParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupSoilTemperatureThresholdParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For T_CAL & T_CAPA temperature thresholds forms */
            setupSoilTemperatureWithHorizonParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupSoilTemperatureWithHorizonParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    horizon: v.number,
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For temperature thresholds forms */
            setupTemperatureThresholdParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupTemperatureThresholdParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For T_WET & T_DRY */
            setupFrostThresholdParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupFrostThresholdParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For wind thresholds forms */
            setupWindThresholdParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupWindThresholdParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** For LW_D sums forms */
            setupLeafWetnessDurationParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupLeafWetnessDurationParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    }),
                    since: v.date,
                    isRepeatable: v.boolean
                });
            },
            /** Rain intensity form */
            setupRainIntensityParams: (args) => {
                const params = getConvertedAlertFieldSettings(args).setupRainIntensityParams;
                const min = params?.threshold?.min;
                const max = params?.threshold?.max;
                return superscheme({
                    threshold: v.between({
                        min,
                        max,
                        strict: false,
                        allowNil: false,
                        errorMessageKey: 'errors.unknown'
                    })
                });
            },
            /** Apple scab infection level form */
            setupAppleScabInfectionChangeParams: superscheme({
                infectionLevels: v.nonEmptyArray(v.enum(alertCompatibleAppleScabLevels))
            })
        }
    },
    shared: {
        mailed: superscheme({
            email: v.email
        }),
        named: superscheme({
            name: v.nonEmptyString
        }),
        headquater: superscheme({
            name: v.nonEmptyString,
            lat: v.number,
            lng: v.number
        })
    },
    clusterSettings: superscheme({
        maxZoom: v.between({
            min: 0,
            max: 21,
            errorMessageKey: 'forms.field_is_invalid',
            strict: false
        }),
        minPointsPerCluster: v.between({ min: 2, max: 30, errorMessageKey: 'forms.field_is_invalid' })
    }),
    inAppExport: superscheme({
        plots: v.union([v.nonEmptyArray(v.number), v.literal('all')]),
        start: v.date,
        end: v.date,
        timeStep: v.enum(exportTimeSteps),
        metrics: v.union([v.nonEmptyArray(v.enum(exportMetrics)), v.literal('all')]),
        format: v.enum(exportFormats)
    })
};
export default schemas;
